import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  DataTable,
  DataTableColumnHeader,
  DataTableSkeleton,
  useDataTable,
} from '@data-delivery/d3s';
import type { UserEntity } from '@data-delivery/permission-ui-api';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

export interface AssignmentUsersProps {
  users: UserEntity[];
  loading: boolean;
  className?: React.HTMLAttributes<HTMLDivElement>['className'];
  assignmentId?: string;
}

export function AssignmentUsers({ users, loading, className, assignmentId }: AssignmentUsersProps) {
  const navigate = useNavigate();
  type Row = UserEntity;
  const columns = useMemo<Array<ColumnDef<Row>>>(
    () => [
      {
        accessorKey: 'loginId',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Login ID' />,
        cell({ row }) {
          const userId = row.original.id;
          const { loginId } = row.original;

          return (
            <button
              type='button'
              className='font-medium text-primary hover:underline'
              onClick={() => {
                if (!userId) {
                  return;
                }

                navigate(`/configuration/users/${userId}/detail`);
              }}
            >
              {loginId}
            </button>
          );
        },
      },
      {
        id: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
        cell({ row }) {
          const { firstName, lastName } = row.original;
          const fullName = [firstName, lastName].filter(Boolean).join(' ');
          return <span>{fullName || '—'}</span>;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'isSystemUser',
        header: ({ column }) => <DataTableColumnHeader column={column} title='System User' />,
        cell({ getValue }) {
          const value = getValue<boolean | undefined>();
          if (value === true) {
            return <span>Yes</span>;
          }

          if (value === false) {
            return <span>No</span>;
          }

          return <span>—</span>;
        },
        enableSorting: false,
      },
      {
        accessorKey: 'orgName',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Org Name' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'rootFolder',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Root Folder' />,
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'serviceAccountOwner',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title='Service Account Owner' />
        ),
        cell: ({ getValue }) => <span>{getValue<string | undefined>() || '—'}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'lastModified',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Last Modified' />,
        cell({ getValue }) {
          const raw = getValue<string | undefined>();
          if (!raw) {
            return <span>—</span>;
          }

          const d = new Date(raw);
          if (Number.isNaN(d.getTime())) {
            return <span>{raw}</span>;
          }

          return <span>{d.toLocaleString()}</span>;
        },
        enableSorting: false,
      },
    ],
    [navigate],
  );
  const table = useDataTable<Row>({ data: users, columns, getRowId: r => r.id });
  const location = useLocation();
  const { businessGroupId, assignmentId: routeAssignmentId } = useParams<{
    businessGroupId?: string;
    assignmentId?: string;
  }>();
  const effectiveAssignmentId = assignmentId || routeAssignmentId;
  return (
    <Card className={cn('flex-1', className)}>
      <CardHeader className='pb-3'>
        <div className='flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
          <div className='flex items-center gap-2'>
            <CardTitle className='text-sm font-medium'>Assigned Users ({users.length})</CardTitle>
          </div>
          <div className='flex flex-wrap gap-2 items-center'>
            <input
              type='text'
              className='h-8 w-52 rounded-md border border-input bg-background px-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
              placeholder='Search users'
              disabled={loading}
              value={(table.getState().globalFilter as string) ?? ''}
              onChange={e => {
                table.setGlobalFilter(e.target.value);
              }}
            />
            <Button
              variant='outline'
              size='sm'
              disabled={loading && !table.getState().globalFilter}
              onClick={() => {
                table.setGlobalFilter('');
              }}
            >
              Reset
            </Button>
            <Button
              variant='outline'
              size='sm'
              disabled={loading}
              onClick={() => {
                const rows = table.getRowModel().rows.map(r => r.original);
                const header = [
                  'Login ID',
                  'Name',
                  'System User',
                  'Org Name',
                  'Root Folder',
                  'Service Account Owner',
                  'Last Modified',
                ];
                const csvRows = rows.map(r => {
                  const fullName = [r.firstName, r.lastName].filter(Boolean).join(' ');
                  return [
                    r.loginId ?? '',
                    fullName,
                    r.isSystemUser ?? false,
                    r.orgName ?? '',
                    r.rootFolder ?? '',
                    r.serviceAccountOwner ?? '',
                    r.lastModified ?? '',
                  ];
                });

                const lines = [header, ...csvRows]
                  .map(cols =>
                    cols
                      .map(val => {
                        const str = String(val ?? '');
                        return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
                      })
                      .join(','),
                  )
                  .join('\n');

                const blob = new Blob([lines], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = 'assignment-users.csv';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
            >
              Export
            </Button>
            {effectiveAssignmentId ? (
              businessGroupId ? (
                <Button asChild size='sm'>
                  <Link
                    to={`/business-groups/${encodeURIComponent(
                      businessGroupId,
                    )}/assignments/${encodeURIComponent(effectiveAssignmentId)}/assign-users`}
                    state={{ backgroundLocation: location }}
                  >
                    Assign Users
                  </Link>
                </Button>
              ) : (
                <Button asChild size='sm'>
                  <Link
                    to={`/configuration/assignments/${encodeURIComponent(
                      effectiveAssignmentId,
                    )}/assign-users`}
                    state={{ backgroundLocation: location }}
                  >
                    Assign Users
                  </Link>
                </Button>
              )
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className='flex flex-col gap-2'>
        {loading ? (
          <DataTableSkeleton
            withPagination={false}
            rowCount={6}
            columnCount={columns.length}
            searchableColumnCount={0}
            filterableColumnCount={0}
            showViewOptions={false}
            cellWidths={['12rem', '10rem']}
            shrinkZero
          />
        ) : users.length === 0 ? (
          <div className='text-xs text-muted-foreground'>No users assigned.</div>
        ) : (
          <div className='rounded-md border'>
            <DataTable
              table={table}
              className='h-[320px]'
              emptyPlaceholderTitle='Users'
              emptyPlaceholderDescription='No users assigned.'
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default AssignmentUsers;
